import type { Ref } from 'react'
import {
  CustomSelect,
  type RenderSearchItemProps,
} from '@/components/inputs/CustomSelect'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { formatMemberOption } from '@/features/iam/members/member-display'
import { observer } from 'mobx-react-lite'
import { type Member } from '@/features/iam/members/Member'
import { truncate } from 'lodash-es'
import { type ClientInputEditorViewModel } from './ClientInputEditorViewModel'
import { SearchResultItem } from '@/components/inputs/SearchResultItem'
import { ClientInputEditor } from '@/features/jobs/TransferJobDialog/ClientInputEditor'

export interface ClientInputEditorContainerProps {
  vm: ClientInputEditorViewModel
}

export const ClientInputEditorContainer = observer(
  function ClientInputEditorContainer({ vm }: ClientInputEditorContainerProps) {
    const selectClientProps = useCustomSelectStateFromItemSource({
      itemSource: vm.fetchClients,
      debounce: 250,
    })
    return (
      <ClientInputEditor
        currentClient={
          vm.jobs.length === 1
            ? {
                avatar: vm.currentClient.avatar,
                displayName: vm.currentClient.displayName,
                online: !!vm.currentClient.user.online,
              }
            : undefined
        }
        clientError={vm.validationClient.getError('client')}
        clientSelect={
          <CustomSelect
            {...selectClientProps}
            testId="transfer-to-client-dropdown"
            multi={false}
            placeholder="Select new client"
            value={vm.client}
            onChange={vm.setClient}
            itemToString={(i) =>
              truncate(formatMemberOption(i), { length: 60 })
            }
            renderSearchItem={(props) => (
              <TransferToClientSearchItemContainer {...props} />
            )}
          />
        }
      />
    )
  },
)

interface TransferToClientSearchItemContainerProps extends RenderSearchItemProps<Member> {
  ref?: Ref<HTMLDivElement>
}

const TransferToClientSearchItemContainer = observer(
  function TransferToClientSearchItemContainer({
    item,
    itemToString: _itemToString,
    ref,
    ...itemProps
  }: TransferToClientSearchItemContainerProps) {
    return (
      <SearchResultItem
        ref={ref}
        {...itemProps}
        testId={
          'transfer-to-client-option-' + item.user.publicId?.toLowerCase()
        }
      >
        {formatMemberOption(item)}
      </SearchResultItem>
    )
  },
)
